/**
 * adsHistory.ts — read-only shapes for concepts (the ideas ads are built from) and research runs (the weekly reports),
 * including everything imported from the Airtable bases.
 */
import { z } from "zod";
import { adChannelSchema, adProductIdSchema } from "./adsProducts.ts";
import { adFeedbackTagSchema, adVerdictSchema } from "./adsFeedback.ts";
import { testKindSchema } from "./adsInsights.ts";
import { adConceptTagsSchema } from "./adsDrafts.ts";

export const CONCEPT_STAGES = ["proposed", "generate_ads", "ads_generated", "skipped"] as const;
export const conceptStageSchema = z.enum(CONCEPT_STAGES);
export type ConceptStage = z.infer<typeof conceptStageSchema>;
export const CONCEPT_STAGE_LABEL: Record<ConceptStage, string> = {
  proposed: "Proposed",
  generate_ads: "Ready for ads",
  ads_generated: "Ads written",
  skipped: "Skipped",
};

export const adConceptSchema = z.object({
  id: z.string(),
  product: adProductIdSchema,
  channel: adChannelSchema,
  title: z.string(),
  angle: z.string(),
  whyItWins: z.string(),
  evidence: z.string(),
  evidenceType: z.string().nullable(),
  evidenceLink: z.string().nullable(),
  graphicDescription: z.string(),
  smallPractice: z.boolean().default(false),
  /** What this idea is testing (format, offer, angle or wording), or null when it is not a test. */
  testKind: testKindSchema.nullable().default(null),
  suggestedBy: z.string().nullable(),
  tags: adConceptTagsSchema.nullable().default(null),
  writtenBy: z.string().nullable().default(null),
  linkedinGroup: z.enum(["company_page", "team_member"]).nullable(),
  score: z.number().int().nullable(),
  scoreReason: z.string(),
  selected: z.boolean(),
  stage: conceptStageSchema,
  researchRunName: z.string().nullable(),
  adCount: z.number().int(),
  createdAt: z.string(),
  /** The latest reviewer verdict on the idea itself, if any. */
  verdict: adVerdictSchema.nullable().default(null),
  reviewTags: z.array(adFeedbackTagSchema).default([]),
  reviewNote: z.string().nullable().default(null),
});
export type AdConcept = z.infer<typeof adConceptSchema>;
export const adConceptsResponseSchema = z.object({ items: z.array(adConceptSchema) });

export const adResearchRunSchema = z.object({
  id: z.string(),
  name: z.string(),
  weekStart: z.string().nullable(),
  source: z.enum(["grok", "claude", "mixed"]),
  status: z.enum(["running", "done", "failed"]),
  sections: z.record(z.string()),
  adCount: z.number().int(),
  conceptCount: z.number().int(),
  createdAt: z.string(),
});
export type AdResearchRun = z.infer<typeof adResearchRunSchema>;
export const adResearchRunsResponseSchema = z.object({ items: z.array(adResearchRunSchema) });

export const adConceptReviewSchema = z.object({
  siteId: z.string().min(1).max(100),
  verdict: adVerdictSchema,
  tags: z.array(adFeedbackTagSchema).max(11).default([]),
  note: z.string().trim().max(1000).optional(),
});
